import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
import { FormInstance, RuleObject } from 'antd/es/form';
// Types
import {
  StreamAffiliateLinkStatus,
  StreamIntervalEnum,
  TicketCompCategory,
  TicketEventOrderSort,
  TicketEventStatus,
  TicketOrderKind,
  TicketOrderStatus,
  TicketRedemptionResult,
  TicketTaxMode,
} from 'api/graphql-global-types';
import { TicketEventListFields } from 'api/ticketing/types/TicketEventListFields';
import { TicketEventOrderRowFields } from 'api/ticketing/types/TicketEventOrderRowFields';
import { TicketingSettingFields } from 'api/ticketing/types/TicketingSettingFields';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';
import { formatCurrencyString } from 'helpers/utils';
// Constants
import {
  isoLocalDateTimeFormat,
  shortDateTimeFormat,
  shortDateTimeZoneFormat,
} from 'constants/global';
import { DATE_TIME_PICKER_PROPS } from 'pages/Events/constants';

export type StatusMeta = {
  label: string;
  color: string;
};

export const EVENT_STATUS_META: Record<TicketEventStatus, StatusMeta> = {
  [TicketEventStatus.Draft]: { label: 'Draft', color: 'default' },
  [TicketEventStatus.OnSale]: { label: 'On sale', color: 'green' },
  [TicketEventStatus.Paused]: { label: 'Paused', color: 'gold' },
  [TicketEventStatus.Closed]: { label: 'Sales closed', color: 'blue' },
  [TicketEventStatus.Postponed]: { label: 'Postponed', color: 'orange' },
  [TicketEventStatus.Cancelled]: { label: 'Cancelled', color: 'red' },
};

/** An event without tickets is only listed, so "on sale" would mislead. */
export const getEventStatusMeta = (
  status: TicketEventStatus,
  ticketsEnabled = true
): StatusMeta =>
  status === TicketEventStatus.OnSale && !ticketsEnabled
    ? { label: 'Published', color: 'green' }
    : EVENT_STATUS_META[status];

export const EVENT_STATUS_FILTER_OPTIONS = Object.values(TicketEventStatus).map(
  (status) => ({ value: status, label: EVENT_STATUS_META[status].label })
);

export type EventLifecycleAction =
  | 'publish'
  | 'pause'
  | 'resume'
  | 'close'
  | 'reopen'
  | 'postpone'
  | 'cancel';

/** Status moves the backend allows from each status (TICKET_EVENT_TRANSITIONS). */
export const EVENT_LIFECYCLE_ACTIONS: Record<
  TicketEventStatus,
  EventLifecycleAction[]
> = {
  [TicketEventStatus.Draft]: ['publish', 'cancel'],
  [TicketEventStatus.OnSale]: ['pause', 'close', 'postpone', 'cancel'],
  [TicketEventStatus.Paused]: ['resume', 'close', 'postpone', 'cancel'],
  [TicketEventStatus.Closed]: ['reopen', 'postpone', 'cancel'],
  [TicketEventStatus.Postponed]: ['cancel'],
  [TicketEventStatus.Cancelled]: [],
};

export const LIFECYCLE_ACTION_TARGET: Record<
  EventLifecycleAction,
  TicketEventStatus
> = {
  publish: TicketEventStatus.OnSale,
  pause: TicketEventStatus.Paused,
  resume: TicketEventStatus.OnSale,
  close: TicketEventStatus.Closed,
  reopen: TicketEventStatus.OnSale,
  postpone: TicketEventStatus.Postponed,
  cancel: TicketEventStatus.Cancelled,
};

export const LIFECYCLE_ACTION_LABEL: Record<EventLifecycleAction, string> = {
  publish: 'Publish',
  pause: 'Pause sales',
  resume: 'Resume sales',
  close: 'Close sales',
  reopen: 'Reopen sales',
  postpone: 'Postpone',
  cancel: 'Cancel event',
};

/** Tiers and the zero-tax approval can only change before sales open or while they are paused. */
export const isEventEditableState = (status: TicketEventStatus): boolean =>
  status === TicketEventStatus.Draft || status === TicketEventStatus.Paused;

export const canApproveZeroTax = (
  status: TicketEventStatus,
  taxMode: TicketTaxMode
): boolean =>
  taxMode === TicketTaxMode.Unconfigured && isEventEditableState(status);

export const isTaxApprovalPending = (
  event: Pick<TicketEventListFields, 'status' | 'taxMode'>
): boolean =>
  event.taxMode === TicketTaxMode.Unconfigured &&
  event.status !== TicketEventStatus.Cancelled;

export const getAddTierAvailability = (
  status: TicketEventStatus
): { allowed: boolean; pausesSales: boolean; reason?: string } => {
  if (isEventEditableState(status)) {
    return { allowed: true, pausesSales: false };
  }

  if (status === TicketEventStatus.OnSale) {
    return { allowed: true, pausesSales: true };
  }

  return {
    allowed: false,
    pausesSales: false,
    reason:
      status === TicketEventStatus.Closed
        ? 'Reopen and pause sales to add a tier.'
        : 'Tiers can no longer be added to this event.',
  };
};

export const canReschedule = (status: TicketEventStatus): boolean =>
  status !== TicketEventStatus.Cancelled;

export const ORDER_STATUS_META: Record<TicketOrderStatus, StatusMeta> = {
  [TicketOrderStatus.Reserved]: { label: 'Reserved', color: 'default' },
  [TicketOrderStatus.PendingPayment]: { label: 'Pending', color: 'gold' },
  [TicketOrderStatus.PaymentReview]: { label: 'In review', color: 'orange' },
  [TicketOrderStatus.Confirmed]: { label: 'Paid', color: 'green' },
  [TicketOrderStatus.Cancelled]: { label: 'Cancelled', color: 'default' },
  [TicketOrderStatus.Expired]: { label: 'Expired', color: 'default' },
  [TicketOrderStatus.RefundPending]: {
    label: 'Refund pending',
    color: 'purple',
  },
  [TicketOrderStatus.Refunded]: { label: 'Refunded', color: 'red' },
};

/** A comp is never paid or refunded: it is active until it is revoked, which cancels it. */
export const getOrderStatusMeta = (
  status: TicketOrderStatus,
  kind: TicketOrderKind
): StatusMeta => {
  if (kind === TicketOrderKind.Comp) {
    return status === TicketOrderStatus.Cancelled
      ? { label: 'Revoked', color: 'default' }
      : { label: 'Comp', color: 'cyan' };
  }

  return ORDER_STATUS_META[status];
};

export const ORDER_STATUS_FILTER_OPTIONS = Object.values(TicketOrderStatus).map(
  (status) => ({ value: status, label: ORDER_STATUS_META[status].label })
);

export const ORDER_SORT_COLUMNS: Record<string, TicketEventOrderSort> = {
  reference: TicketEventOrderSort.Reference,
  customer: TicketEventOrderSort.CustomerName,
  tickets: TicketEventOrderSort.AdmissionCount,
  amount: TicketEventOrderSort.Amount,
  createdAt: TicketEventOrderSort.CreatedAt,
  status: TicketEventOrderSort.Status,
};

export const REFUNDABLE_ORDER_STATUSES = [
  TicketOrderStatus.Confirmed,
  TicketOrderStatus.PaymentReview,
];

export const canRefundOrder = (
  status: TicketOrderStatus,
  kind: TicketOrderKind
): boolean =>
  kind === TicketOrderKind.Purchase &&
  REFUNDABLE_ORDER_STATUSES.includes(status);

/** The fields every order list shares, whichever query the row came from. */
export type OrderRow = Omit<TicketEventOrderRowFields, '__typename'>;

export const getCardLabel = (
  order: Pick<OrderRow, 'cardBrand' | 'cardLast4'>
): string =>
  order.cardBrand && order.cardLast4
    ? `${order.cardBrand} •••• ${order.cardLast4}`
    : '';

export const getTicketsLabel = (order: Pick<OrderRow, 'lines'>): string =>
  order.lines
    .map((line) => `${line.admissionCount} × ${line.tierLabel}`)
    .join(', ');

export const getTicketSerials = (order: Pick<OrderRow, 'lines'>): string[] =>
  order.lines.flatMap((line) => line.ticketSerials);

export type RefundTrackedOrder = Pick<
  TicketEventOrderRowFields,
  | 'status'
  | 'refundRequestedAt'
  | 'refundRequestedBy'
  | 'refundedAt'
  | 'refundFailure'
>;

export type RefundNote = {
  failed: boolean;
  text: string;
  reason?: string;
};

/** What happened to the latest refund, in a line for the orders table. */
export const getRefundNote = (
  order: RefundTrackedOrder,
  timezone?: string | null
): RefundNote | null => {
  if (order.status === TicketOrderStatus.Refunded && order.refundedAt) {
    return {
      failed: false,
      text: `Refunded ${formatEventDateTime(order.refundedAt, timezone)}`,
    };
  }

  if (
    order.status === TicketOrderStatus.RefundPending &&
    order.refundRequestedAt
  ) {
    const by = order.refundRequestedBy?.name;

    return {
      failed: false,
      text: `Requested ${formatEventDateTime(
        order.refundRequestedAt,
        timezone
      )}${by ? ` by ${by}` : ''}`,
    };
  }

  if (REFUNDABLE_ORDER_STATUSES.includes(order.status) && order.refundFailure) {
    return {
      failed: true,
      text: `Refund failed ${formatEventDateTime(
        order.refundFailure.failedAt,
        timezone
      )}`,
      reason: order.refundFailure.reason,
    };
  }

  return null;
};

export const canRevokeComp = (
  status: TicketOrderStatus,
  kind: TicketOrderKind
): boolean =>
  kind === TicketOrderKind.Comp && status === TicketOrderStatus.Confirmed;

export const COMP_CATEGORY_OPTIONS = Object.values(TicketCompCategory).map(
  (category) => ({ value: category, label: category })
);

export const SCAN_RESULT_META: Record<TicketRedemptionResult, StatusMeta> = {
  [TicketRedemptionResult.Admitted]: { label: 'Admitted', color: 'green' },
  [TicketRedemptionResult.AlreadyAdmitted]: {
    label: 'Already used',
    color: 'orange',
  },
  [TicketRedemptionResult.Revoked]: { label: 'Revoked', color: 'red' },
  [TicketRedemptionResult.WrongEvent]: { label: 'Wrong event', color: 'red' },
  [TicketRedemptionResult.Invalid]: { label: 'Invalid', color: 'red' },
  [TicketRedemptionResult.DoorsNotOpen]: {
    label: 'Doors not open',
    color: 'gold',
  },
  [TicketRedemptionResult.AdmissionClosed]: {
    label: 'Admission closed',
    color: 'gold',
  },
  [TicketRedemptionResult.EventCancelled]: {
    label: 'Event cancelled',
    color: 'red',
  },
  [TicketRedemptionResult.EventPostponed]: {
    label: 'Event postponed',
    color: 'orange',
  },
  [TicketRedemptionResult.EventNotPublished]: {
    label: 'Not published',
    color: 'default',
  },
};

export const SCAN_RESULT_FILTER_OPTIONS = Object.values(
  TicketRedemptionResult
).map((result) => ({ value: result, label: SCAN_RESULT_META[result].label }));

export const formatCount = (value: number): string =>
  value.toLocaleString('en-US');

export const pluralize = (
  count: number,
  singular: string,
  plural = `${singular}s`
): string => `${formatCount(count)} ${count === 1 ? singular : plural}`;

export const formatMoney = (value?: number | null): string =>
  value === null || value === undefined ? '—' : formatCurrencyString(value);

export const formatEventDateTime = (
  value?: string | null,
  timezone?: string | null
): string => {
  if (!value) {
    return '—';
  }

  const date = timezone
    ? momentTimezone(value).tz(timezone)
    : momentTimezone(value);

  return date.format(timezone ? shortDateTimeZoneFormat : shortDateTimeFormat);
};

/**
 * The pickers hold wall-clock time; the backend wants the instant that clock
 * time names in the venue's zone, whatever zone the admin's browser is in.
 */
export const toVenueInstant = (value: Moment, timezone: string): string =>
  momentTimezone
    .tz(value.format(isoLocalDateTimeFormat), timezone)
    .toISOString();

export const fromVenueInstant = (value: string, timezone: string): Moment =>
  moment(momentTimezone(value).tz(timezone).format(isoLocalDateTimeFormat));

/** Picker values are venue wall-clock times, so they compare with now only once converted. */
export const isFutureInVenue = (value: Moment, timezone?: string): boolean =>
  moment(timezone ? toVenueInstant(value, timezone) : value).isAfter(moment());

const venueNow = (timezone: string): Moment =>
  fromVenueInstant(new Date().toISOString(), timezone);

type PickerShowTime = typeof DATE_TIME_PICKER_PROPS.showTime;

type VenuePickerProps = Omit<typeof DATE_TIME_PICKER_PROPS, 'showTime'> & {
  showNow?: boolean;
  showTime: PickerShowTime & { defaultValue?: Moment };
};

type VenueRangePickerProps = Omit<typeof DATE_TIME_PICKER_PROPS, 'showTime'> & {
  showTime: PickerShowTime & { defaultValue: [Moment, Moment] };
};

/** A picked day starts at the venue's current time, not the browser's; "Now" would read the browser clock. */
export const venueDateTimePickerProps = (timezone?: string): VenuePickerProps =>
  timezone
    ? {
        ...DATE_TIME_PICKER_PROPS,
        showNow: false,
        showTime: {
          ...DATE_TIME_PICKER_PROPS.showTime,
          defaultValue: venueNow(timezone),
        },
      }
    : DATE_TIME_PICKER_PROPS;

export const venueDateTimeRangePickerProps = (
  timezone: string
): VenueRangePickerProps => {
  const now = venueNow(timezone);

  return {
    ...DATE_TIME_PICKER_PROPS,
    showTime: {
      ...DATE_TIME_PICKER_PROPS.showTime,
      defaultValue: [now, now.clone()],
    },
  };
};

export const formatTimezoneName = (timezone: string): string =>
  timezone.replace(/_/g, ' ');

export const getTimezoneLabel = (timezone: string, at?: string): string => {
  const date = at
    ? momentTimezone(at).tz(timezone)
    : momentTimezone.tz(timezone);

  return `${formatTimezoneName(timezone)} (${date.format('z, [UTC]Z')})`;
};

export const futureInVenueRule = (
  timezone: string | undefined,
  message: string
): RuleObject => ({
  validator: (_, value?: Moment | null) =>
    !value || isFutureInVenue(value, timezone)
      ? Promise.resolve()
      : Promise.reject(new Error(message)),
});

export const REPEAT_LABELS: Record<StreamIntervalEnum, string> = {
  [StreamIntervalEnum.weekly]: 'Every week',
  [StreamIntervalEnum.biweekly]: 'Every other week',
  [StreamIntervalEnum.monthly]: 'Every month',
};

export const formatVenueAddress = (venue: {
  addressLine1?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}): string =>
  [
    venue.addressLine1,
    venue.city,
    [venue.region, venue.postalCode].filter(Boolean).join(' '),
    venue.country,
  ]
    .filter(Boolean)
    .join(', ');

export type TierDraft = {
  label?: string;
  price?: number | null;
  capacity?: number | null;
};

export type TiersSummary = {
  count: number;
  ticketsAvailable: number;
  maxRevenue: number;
};

export const getTiersSummary = (tiers: TierDraft[]): TiersSummary =>
  tiers.reduce<TiersSummary>(
    (summary, tier) => ({
      count: summary.count + 1,
      ticketsAvailable: summary.ticketsAvailable + (tier.capacity || 0),
      maxRevenue: summary.maxRevenue + (tier.price || 0) * (tier.capacity || 0),
    }),
    { count: 0, ticketsAvailable: 0, maxRevenue: 0 }
  );

const STRIPE_MIN_CHARGE = 0.5;
const DEFAULT_MAX_TICKET_PRICE = 100000;

/** The platform's price bounds, never below what Stripe can charge. */
export const getTicketPriceLimits = (
  setting?: Pick<TicketingSettingFields, 'minTicketPrice' | 'maxTicketPrice'>
): { minTicketPrice: number; maxTicketPrice: number } => ({
  minTicketPrice: Math.max(
    setting?.minTicketPrice ?? STRIPE_MIN_CHARGE,
    STRIPE_MIN_CHARGE
  ),
  maxTicketPrice: setting?.maxTicketPrice ?? DEFAULT_MAX_TICKET_PRICE,
});

const TICKETING_ERROR_MESSAGES: Record<string, string> = {
  StaleVersion:
    'Someone else changed this event a moment ago. Reload it and try again.',
  TicketingDisabled:
    'Ticketing is switched off. Turn it on in Events → Settings first.',
  TaxUnconfigured: 'Approve the zero-tax policy before opening sales.',
};

/** Null when a field is invalid; antd already shows the field errors. */
export const validateForm = <Values>(
  form: FormInstance<Values>
): Promise<Values | null> => form.validateFields().catch(() => null);

export const getTicketingErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const { message, graphQLErrors } = (error || {}) as {
    message?: string;
    graphQLErrors?: { extensions?: { response?: { ticketCode?: string } } }[];
  };
  const code = graphQLErrors?.[0]?.extensions?.response?.ticketCode;

  if (code && TICKETING_ERROR_MESSAGES[code]) {
    return TICKETING_ERROR_MESSAGES[code];
  }

  return getProperErrorMessage(error, message || fallback);
};

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

/** The affiliate's share of what the host makes on a sale, as web shows it. */
export const calculateAffiliateCommission = (
  netAmount: number,
  percent?: number | null
): number =>
  netAmount > 0 && percent && percent > 0
    ? roundMoney((netAmount * percent) / 100)
    : 0;

export const formatMoneyRange = (amounts: number[]): string => {
  if (!amounts.length) {
    return formatMoney(0);
  }

  const min = Math.min(...amounts);
  const max = Math.max(...amounts);

  return min === max
    ? formatMoney(min)
    : `${formatMoney(min)}–${formatMoney(max)}`;
};

export const getAffiliateEarningsRange = (
  ticketPrices: number[],
  percent?: number | null
): string =>
  formatMoneyRange(
    (ticketPrices.length ? ticketPrices : [0]).map((price) =>
      calculateAffiliateCommission(price, percent)
    )
  );

export const getCommissionRules = (required: boolean): RuleObject[] => [
  { required, message: 'Set the commission' },
  { type: 'number', min: 0, max: 100, message: 'From 0% to 100%' },
];

type PersonLike = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  storeDetails?: { storeName?: string | null } | null;
};

export const getPersonName = (
  person?: PersonLike | null,
  fallback = 'Unnamed'
): string =>
  person?.storeDetails?.storeName ||
  [person?.firstName, person?.lastName].filter(Boolean).join(' ') ||
  person?.email ||
  fallback;

export const getEventHostName = (
  event: Pick<TicketEventListFields, 'host' | 'hostUserId'>
): string => event.host?.storeDetails?.storeName || event.hostUserId;

export const AFFILIATE_STATUS_META: Record<
  StreamAffiliateLinkStatus,
  StatusMeta
> = {
  [StreamAffiliateLinkStatus.Active]: { label: 'Active', color: 'green' },
  [StreamAffiliateLinkStatus.Disabled]: { label: 'Disabled', color: 'default' },
};
